import { COLORS } from '@btime/constants'
import { prop } from 'ramda'
import React from 'react'

import { useForm, useFormState } from '../../../../../packages/kiti/components/form/form/FormHandler'
import { useIntegrations } from '../../_load/IntegrationsLoader'
import { Title } from '../../../../../packages/kiti/components/presentation/Text'
import FormItem from '../../../../../packages/kiti/components/form/form/FormItem'
import Separator from '../../../../../packages/kiti/components/helpers/Separator'
import SeparatorWrapper from '../../../../../components/helpers/SeparatorWrapper'
import { INTEGRATION_DATA_FIELDS } from '../../_data/integrationDataFields'
import Scroll from '../../../../../packages/kiti/components/helpers/Scroll'
import MaskSelect from '../../../../../containers/mask/select/MaskSelect'
import CheckboxInput from '../../../../../components/form/CheckboxInput'
import FormItems from '../../../../../components/form/FormItem'
import Select from '../../../../../packages/kiti/components/form/Select'
import { HorizontalLine } from '../../../../../components/shapes/Line'
import Border from '../../../../../components/helpers/Border'
import SelectFieldsOptions from './SelectFieldsOptions'
import { useTranslation } from 'react-i18next'
import { ALLOWED_QUESTIONS_AND_FIELDS_INTEGRATIONS } from '../../../../integrations/_services/services'

const MaskSection = ({ onChange, value, type }) => {
  const { t } = useTranslation('editFields')
  if (type !== 'text') return false

  return (
    <>
      <Separator weight={6} />
      <Title size="h6" color={COLORS.dark4}>
        {t('maskConfig')}
      </Title>
      <HorizontalLine thin lineColor={COLORS.light2} />
      <Separator weight={2} />
      <MaskSelect onChange={onChange} value={value} />
    </>
  )
}

const IntegrationSection = ({ onChange, onSelectService, value, type }) => {
  const { t } = useTranslation('editFields')
  const integrations = useIntegrations()

  if (type !== 'integration') return false

  const selectedIntegration = value?.configuration?.selectedIntegrationService

  const availableIntegrationServices = integrations.filter(field =>
    ALLOWED_QUESTIONS_AND_FIELDS_INTEGRATIONS.includes(field.integrationService)
  )

  const integrationFieldsMatch = INTEGRATION_DATA_FIELDS.filter(field =>
    selectedIntegration?.integrationService?.includes(field.integrationService)
  )

  const integrationFields = integrationFieldsMatch.map(field => ({
    ...field,
    integrationId: integrations.find(item => item.integrationService === field.integrationService)?.integrationId
  }))

  return (
    <>
      <Separator weight={6} />
      <Title size="h6" color={COLORS.dark4}>
        {t('integrationFields')}
      </Title>
      <HorizontalLine thin lineColor={COLORS.light2} />
      <Separator weight={2} />
      <FormItems label={t('service')} weight={2}>
        <Select
          options={availableIntegrationServices}
          onChange={onSelectService}
          value={value?.configuration?.selectedIntegrationService}
        />
      </FormItems>
      {selectedIntegration && (
        <FormItems label={t('field')}>
          <Select options={integrationFields} onChange={onChange} value={value?.configuration} />
        </FormItems>
      )}
    </>
  )
}

const FieldForm = () => {
  const form = useForm()
  const { handleChangeIn } = useFormState()
  const { t } = useTranslation('editFields')
  const { t: tCommon } = useTranslation('common')

  const index = form.findIndex(prop('__selected'))
  const field = form[index]
  const title = tCommon('fieldTypes.' + field?.type)

  const handleSetIntegration = value => {
    const selectedIntegrationService = { ...field?.configuration?.selectedIntegrationService }
    const integrationId = field?.configuration?.selectedIntegrationService?.integrationId
    const newValue = { ...value, selectedIntegrationService, integrationId }

    handleChangeIn([index, 'configuration'], newValue)
    handleChangeIn([index, 'name'], value?.name)
    handleChangeIn([index, 'required'], value?.required)
  }

  const handleSelectIntegrationService = value => {
    handleChangeIn([index, 'configuration'], {
      selectedIntegrationService: value
    })
    handleChangeIn([index, 'name'], undefined)
    handleChangeIn([index, 'required'], undefined)
  }

  if (!field) return false

  return (
    <Scroll scrollY maxHeight="75vh" height="100%">
      <Border noTop noRight noLeft noRadius>
        <SeparatorWrapper usePadding weight={4}>
          <Title noMargin size="h5">
            {title}
          </Title>
        </SeparatorWrapper>
      </Border>
      <SeparatorWrapper weight={4}>
        <FormItem path={[index, 'name']} autoFocus label={t('label')} disabled={form[index]?.type === 'integration'} />
        <Separator weight={4} />
        <Title size="h6" color={COLORS.dark4}>
          {t('questionConfig')}
        </Title>
        <HorizontalLine thin lineColor={COLORS.light2} />
        <Separator weight={2} />

        <Separator weight={2} />
        <CheckboxInput
          onChange={handleChangeIn([index, 'required'])}
          value={form[index]?.required}
          label={t('required')}
          disabled={form[index]?.type === 'integration'}
        />
        <Separator weight={2} />
        <CheckboxInput
          onChange={handleChangeIn([index, 'configuration', 'confidential'])}
          value={form[index]?.configuration?.confidential}
          label={t('confidential')}
        />
        {field?.type === 'select' && (
          <>
            <Separator weight={2} />
            <CheckboxInput
              label={t('displayAsList')}
              value={form[index]?.configuration?.displayAsList}
              onChange={handleChangeIn([index, 'configuration', 'displayAsList'])}
            />
          </>
        )}
        <MaskSection onChange={handleChangeIn([index, 'masks'])} value={form[index]?.masks} type={form[index]?.type} />

        <IntegrationSection
          onChange={handleSetIntegration}
          onSelectService={handleSelectIntegrationService}
          value={form[index]}
          type={form[index]?.type}
        />

        <Separator weight={4} />
        {field?.type === 'select' && (
          <>
            <Title size="h6" color={COLORS.dark4}>
              {t('multipleChoiceOptions')}
            </Title>
            <HorizontalLine thin lineColor={COLORS.light2} />
            <Separator weight={2} />
            <MaskSection
              onChange={handleChangeIn([index, 'masks'])}
              value={form[index]?.masks}
              type={form[index]?.type}
            />
            <SelectFieldsOptions
              onChange={handleChangeIn([index, 'options'])}
              value={form[index]?.options}
              type={form[index]?.type}
            />
          </>
        )}
      </SeparatorWrapper>
    </Scroll>
  )
}

export default FieldForm
